// ui/Shell.tsx
import React, { useEffect, useState } from "react";
import type { Session } from "./auth";
import { Sidebar, type NavKey } from "./Sidebar";

import { LegalMattersPage } from "./pages/LegalMattersPage";
import { IpMattersPage } from "./pages/IpMattersPage";
import { BrandAssetsPage } from "./pages/BrandAssetsPage";
import { LawyerCoopPage } from "./pages/LawyerCoopPage";
import { ContractsPage } from "./pages/ContractsPage";
import { ContractReviewsPage } from "./pages/ContractReviewsPage";
import { CalendarPage } from "./pages/CalendarPage";
import TemplatesPage from "./pages/TemplatesPage";
import { GovernanceCoreDocsPage } from "./pages/GovernanceCoreDocsPage";
import { TasksAndLogsPage } from "./pages/TasksAndLogsPage";
import { GovCorrespondencePage } from "./pages/GovCorrespondencePage";
import { SealApplicationPage } from "./pages/SealApplicationPage";
import { AuditLogPage } from "./pages/AuditLogPage";
import SystemDebugPage from "./pages/StorageDebugPage";
import { NotificationHistoryPage } from "./pages/NotificationHistoryPage";
import { ComplianceControlCenterPage } from "./pages/ComplianceControlCenterPage";
import { LegacyContractsPage } from "./pages/LegacyContractsPage";

import { GlobalSearchBar } from "./components/GlobalSearchBar";
import { AIAssistantDrawer } from "./ai/AIAssistantDrawer";
import { AiFloatingButton } from "./ai/AiFloatingButton";
import { GlobalProvider, useAiContext } from "./ai/AiContext";
import { ToastProvider, useToast } from "./components/Toast";
import { ConfirmDeleteModal } from "./components/ConfirmDeleteModal";

import { useAuth } from "./context/AuthContext";
import { fetchDashboardStats, type DashboardStats } from "./services/dashboard";
import { getCalendarEventsBetween } from "./services/calendarService";
import { markAllAsRead } from "./services/notificationService";

import { db } from "../firebaseClient";
import type { NotificationDoc, CalendarEventDoc } from "./types/legalOps";

import { getUserPreferences } from "./services/userPreferences";
import { canViewWorklog, canViewAuditLog } from "./config/roles";
import { formatDate, formatDateMD, compareDateLike } from "./utils/dateUtils";

import {
  getTodaySummary,
  type DailySummaryResult,
  type UserRealtimeSummary,
  type PendingItem,
  triggerDailySummaryEmail,
} from "./services/dailySummaryService";

import { subscribeDashboardTodos } from "./services/todosService";
import { DAILY_WARM_TIPS } from "./constants/dailyWarmTips";

// Helper: Pick a daily warm quote
function pickWarmQuote(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % DAILY_WARM_TIPS.length;
  return DAILY_WARM_TIPS[index];
}

const TITLES: Record<string, { title: string; desc: string }> = {
  dashboard: { title: "法務總攬儀錶板", desc: "關鍵指標、待辦提醒與近期更新。" },
  contracts: { title: "合約系統管理", desc: "合約生命週期、審閱與簽署進度。" },
  contractReviews: { title: "合約草案審查", desc: "合約審閱案件、優先順序與時效控管。" },
  legalMatters: { title: "案件及爭議管理", desc: "訴訟、非訟、消費爭議與其他紛爭處理。" },
  ipMatters: { title: "智財申請與維權", desc: "商標、專利、著作權申請與侵權維權。" },
  brandAssets: { title: "品牌資產管理", desc: "商標、IP 授權與品牌佈局導圖。" },
  lawyerCoop: { title: "顧問律師管理", desc: "外部律師事務所與顧問合作清單。" },
  sealApplication: { title: "用印申請與紀錄", desc: "公司大小章、合約用印流程追蹤。" },
  govCorrespondence: { title: "公文函覆", desc: "主管機關來函、裁處與公文回覆追蹤。" },
  templates: { title: "範本中心", desc: "標準合約與法律文件範本庫。" },
  governanceCoreDocs: { title: "公司治理核心文件庫", desc: "董事會、內控制度與風險管理辦法。" },
  tasksAndLogs: { title: "待辦事項與法務工作日誌", desc: "法務團隊工作清單與處理歷程。" },
  audit: { title: "稽核、簽核及操作紀錄", desc: "系統操作歷程、簽核歷程與稽核軌跡。" },
  calendar: { title: "行事曆", desc: "案件、爭議、合約到期與授權期限月曆。" },
  notificationHistory: { title: "通知歷史紀錄", desc: "系統發送之所有通知與提醒。" },
  storageDebug: { title: "系統檢測 (Debug)", desc: "檔案上傳測試與報表預覽" },
  complianceControlCenter: {
    title: "法遵情資與專案管控中心",
    desc: "法規變動監測、公司內部影響評估與專案追蹤。",
  },
  legacyContracts: {
    title: "舊合約登錄系統 (舊合約建檔專用)",
    desc: "僅用於輸入既有紙本／掃描舊合約的基本資料，送出後將併入主合約資料庫。",
  },
};

function DashboardUserCard({
  user,
  headerColor,
}: {
  user: UserRealtimeSummary;
  headerColor: string;
}) {
  const { pendingList: pending, dueSoon, agenda } = user;
  const [expanded, setExpanded] = useState(false);

  const warmQuote = pickWarmQuote(user.userKey);

  const countsAny = (user as any).counts ?? {};
  const contractsCount = Number(countsAny.contracts ?? 0);
  const sealsCount = Number(countsAny.seals ?? 0);
  const totalCount =
    countsAny.total != null
      ? Number(countsAny.total)
      : contractsCount + sealsCount + Number(countsAny.others ?? 0);
  const otherCount =
    countsAny.others != null
      ? Number(countsAny.others)
      : Math.max(0, totalCount - contractsCount - sealsCount);

  const renderSection = (title: string, items: PendingItem[]) => (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          fontWeight: "600",
          color: "#e5e7eb",
          marginBottom: 4,
          fontSize: 13,
        }}
      >
        {title}（{items.length} 件）
      </div>
      {items.length === 0 ? (
        <div style={{ color: "#6b7280", paddingLeft: 14, fontSize: 12 }}>無</div>
      ) : (
        <ol
          style={{
            margin: 0,
            paddingLeft: 24,
            color: "#9ca3af",
            listStyleType: "decimal",
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          {items.map((item) => (
            <li
              key={`${item.module}-${item.id}`}
              style={{ marginBottom: 4 }}
            >
              <span
                style={{
                  fontFamily: "monospace",
                  marginRight: 6,
                  color: "#fbbf24",
                }}
              >
                {item.id}
              </span>
              {item.title}
            </li>
          ))}
        </ol>
      )}
    </div>
  );

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid #333",
        borderRadius: 12,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          paddingBottom: 8,
        }}
      >
        <div style={{ fontWeight: "bold", color: headerColor, fontSize: 15 }}>
          {user.displayName}
        </div>
        <div style={{ display: "flex", gap: 6, fontSize: 11 }}>
          <span
            style={{
              background: "rgba(59, 130, 246, 0.2)",
              color: "#93c5fd",
              padding: "2px 6px",
              borderRadius: 4,
            }}
          >
            合約 {contractsCount}
          </span>
          <span
            style={{
              background: "rgba(168, 85, 247, 0.2)",
              color: "#e9d5ff",
              padding: "2px 6px",
              borderRadius: 4,
            }}
          >
            用印 {sealsCount}
          </span>
          <span
            style={{
              background: "rgba(75, 85, 99, 0.4)",
              color: "#d1d5db",
              padding: "2px 6px",
              borderRadius: 4,
            }}
          >
            其他 {otherCount}
          </span>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
        {/* 1. Agenda */}
        <div>
          <div style={{ fontSize: 12, fontWeight: "bold", color: "#60a5fa", marginBottom: 4 }}>
            📅 今日行程
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#ddd", lineHeight: 1.5 }}>
            {agenda.map((it, i) => (
              <li key={i}>{it}</li>
            ))}
          </ul>
        </div>

        {/* 2. Deadlines */}
        <div>
          <div style={{ fontSize: 12, fontWeight: "bold", color: "#f87171", marginBottom: 4 }}>
            ⏰ 即將到期案件
          </div>
          <div style={{ fontSize: 13, color: "#ccc", paddingLeft: 4 }}>
            <div style={{ marginBottom: 4 }}>
              <span style={{ color: dueSoon.today.length > 0 ? "#ef4444" : "#6b7280" }}>
                本日到期（{dueSoon.today.length} 件）
              </span>
              {dueSoon.today.length > 0 && (
                <span style={{ fontSize: 11, marginLeft: 6 }}>
                  ({dueSoon.today[0].title}...)
                </span>
              )}
            </div>
            <div style={{ marginBottom: 4 }}>
              <span style={{ color: dueSoon.within30.length > 0 ? "#fbbf24" : "#6b7280" }}>
                30 天內到期（{dueSoon.within30.length} 件）
              </span>
            </div>
            <div>
              <span style={{ color: dueSoon.within60.length > 0 ? "#f59e0b" : "#6b7280" }}>
                60 天內到期（{dueSoon.within60.length} 件）
              </span>
            </div>
          </div>
        </div>

        {/* 3. Pending */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: "bold", color: "#fbbf24", marginBottom: 8 }}>
            📝 待簽核事項
          </div>
          <div style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 8, color: "#9ca3af" }}>
              總計待辦：{totalCount} 件
            </div>

            <button
              type="button"
              style={{
                background: "transparent",
                border: "none",
                color: "#60a5fa",
                fontSize: 12,
                cursor: "pointer",
                padding: 0,
                textDecoration: "underline",
              }}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "收合工作內容 ▲" : "展開全部工作內容 ▼"}
            </button>

            {expanded && (
              <div style={{ marginTop: 12, borderTop: "1px dashed #444", paddingTop: 12 }}>
                {renderSection("合約待簽", pending.contracts)}
                {renderSection("用印待簽", pending.seals)}
                {renderSection("案件與爭議", pending.cases)}
                {renderSection("制式合約範本庫", pending.templates)}
                {renderSection("公司治理 / 其他", pending.others)}
                {pending.governance.length > 0 && renderSection("公司治理", pending.governance)}
              </div>
            )}
          </div>
        </div>

        {/* 4. Warm Quote */}
        <div style={{ marginTop: "auto", paddingTop: 10, borderTop: "1px dashed #444" }}>
          <div className="text-xs text-green-400 mt-2">
            <span className="font-semibold">＊ 溫馨小語</span>
            <p>{warmQuote}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function LegalDashboardDailySummaryCard({ onNavigate }: { onNavigate: (key: NavKey) => void }) {
  const { showToast } = useToast();
  const [data, setData] = useState<DailySummaryResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setLoading(true);
    let retryTimeout: any;

    const unsubscribe = subscribeDashboardTodos(
      (updatedData) => {
        setData(updatedData);
        setLoading(false);
        setError(null);
        setRetryCount(0);
      },
      (err) => {
        console.error("Dashboard Subscription Error:", err);
        setError("監控連線中斷，嘗試重連...");

        getTodaySummary()
          .then((res) => {
            setData(res);
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });

        retryTimeout = setTimeout(() => {
          setRetryCount((c) => c + 1);
        }, 5000);
      }
    );

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
      clearTimeout(retryTimeout);
    };
  }, [retryCount]);

  const handleSendEmail = async () => {
    setSending(true);
    try {
      await triggerDailySummaryEmail();
      showToast("已送出寄信請求，系統處理中...");
    } catch (e) {
      console.error(e);
      showToast("寄送失敗", "error");
    } finally {
      setSending(false);
    }
  };

  const manualRefresh = () => {
    setLoading(true);
    setRetryCount((c) => c + 1);
  };

  const goToDebug = () => {
    window.history.pushState({}, "", "?tab=dashboard_todos_debug");
    onNavigate("storageDebug");
  };

  if (loading && !data) {
    return (
      <div className="section" style={{ padding: 20, textAlign: "center", color: "#666" }}>
        連線至待辦中心 (Todos) 中...
      </div>
    );
  }

  const showErrorWarning = error ? (
    <div style={{ fontSize: 11, color: "#f87171", marginBottom: 8 }}>⚠️ {error}</div>
  ) : null;

  if (!data) {
    return (
      <div className="section" style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>
        {error || "目前無法取得待辦摘要。"}
        <button className="smallbtn" onClick={manualRefresh} style={{ marginLeft: 10 }}>
          重試
        </button>
      </div>
    );
  }

  const kevin = data.users.find((u) => u.userKey === "kevin");
  const lisa = data.users.find((u) => u.userKey === "lisa");
  const irene = data.users.find((u) => u.userKey === "irene");

  const { emailStatus } = data;
  const emailStatusColor =
    emailStatus?.status === "success"
      ? "#10b981"
      : emailStatus?.status === "error"
      ? "#ef4444"
      : "#6b7280";

  return (
    <div className="section" style={{ marginBottom: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #333",
          paddingBottom: 10,
          marginBottom: 16,
        }}
      >
        <div>
          <div style={{ fontWeight: "bold", fontSize: 16, color: "#fff" }}>
            今日待辦摘要 (Live via Todos)
            <span
              style={{
                fontSize: 11,
                fontWeight: "normal",
                marginLeft: 8,
                color: error ? "#f87171" : "#10b981",
              }}
            >
              ● {error ? "連線不穩" : "即時同步中"}
            </span>
            <button
              onClick={goToDebug}
              style={{
                marginLeft: 10,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: 12,
                color: "#aaa",
                textDecoration: "underline",
              }}
              title="前往訊號偵錯"
            >
              🛠️ Debug
            </button>
          </div>

          {showErrorWarning}

          {emailStatus && emailStatus.status !== "n/a" && (
            <div style={{ fontSize: 11, color: emailStatusColor, marginTop: 4 }}>
              {emailStatus.status === "success"
                ? `✅ 今日 summary 寄信狀態：已寄出 (${emailStatus.info})`
                : emailStatus.status === "error"
                ? `❌ 今日 summary 寄信狀態：失敗 (${emailStatus.info})`
                : "⚪ 今日 summary 寄信狀態：尚未寄出"}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button className="smallbtn" onClick={manualRefresh} style={{ fontSize: 11 }}>
            🔄 重新連線
          </button>
          <button
            className="smallbtn"
            onClick={handleSendEmail}
            disabled={sending}
            style={{
              fontSize: 11,
              display: "flex",
              gap: 4,
              alignItems: "center",
              background: sending ? "#333" : "#3b82f6",
              border: "none",
              color: "white",
            }}
          >
            {sending ? "處理中..." : "✉️ 寄信"}
          </button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 16,
          alignItems: "stretch",
        }}
      >
        {kevin && <DashboardUserCard user={kevin} headerColor="#fbbf24" />}
        {lisa && <DashboardUserCard user={lisa} headerColor="#a78bfa" />}
        {irene && <DashboardUserCard user={irene} headerColor="#34d399" />}
      </div>

      <div style={{ textAlign: "right", fontSize: 11, color: "#555", marginTop: 12 }}>
        資料來源：Firestore / todos collection
      </div>
    </div>
  );
}

function ShellInner({
  session,
  onLogout,
  bootErr,
}: {
  session: Session;
  onLogout: () => void;
  bootErr?: any;
}) {
  const { isSealUser, user } = useAuth();
  const { showToast } = useToast();

  useEffect(() => {
    if (bootErr) console.error("Boot error:", bootErr);
  }, [bootErr]);

  const [active, setActive] = useState<NavKey>(() => {
    if (session.role === "legacyDataEntry") return "legacyContracts";
    if (session.role === "sealUser") return "contractReviews";
    if (session.role === "dept-minimal") return "sealApplication";
    return "dashboard";
  });

  const { setActiveItem, isAiOpen, setAiOpen, deleteDialogState, closeDeleteDialog } =
    useAiContext();

  const [notifs, setNotifs] = useState<NotificationDoc[]>([]);
  const [dashStats, setDashStats] = useState<DashboardStats | null>(null);
  const [recentEvents, setRecentEvents] = useState<CalendarEventDoc[]>([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  useEffect(() => {
    if (
      user?.id &&
      !isSealUser &&
      session.role !== "dept-minimal" &&
      session.role !== "legacyDataEntry" &&
      !prefsLoaded
    ) {
      getUserPreferences(user.id).then((prefs) => {
        if (active === "dashboard" && prefs.defaultModule && prefs.defaultModule !== "dashboard") {
          if (TITLES[prefs.defaultModule]) {
            setActive(prefs.defaultModule as NavKey);
          }
        }
        setPrefsLoaded(true);
      });
    }
  }, [user, isSealUser, session.role, prefsLoaded, active]);

  useEffect(() => {
    if (!isSealUser && session.role !== "dept-minimal" && session.role !== "legacyDataEntry") {
      const q = db
        .collection("notifications")
        .where("read", "==", false)
        .orderBy("createdAt", "desc")
        .limit(20);

      const unsubscribe = q.onSnapshot((snapshot: any) => {
        const dbNotifs = snapshot.docs.map(
          (d: any) => ({ id: d.id, ...d.data() } as NotificationDoc)
        );
        setNotifs(dbNotifs);
      });

      return () => unsubscribe();
    } else {
      setNotifs([]);
    }
  }, [isSealUser, session.role]);

  useEffect(() => {
    if (
      active === "dashboard" &&
      !isSealUser &&
      session.role !== "dept-minimal" &&
      session.role !== "legacyDataEntry"
    ) {
      fetchDashboardStats().then(setDashStats).catch(console.error);

      const today = new Date();
      const next30 = new Date();
      next30.setDate(today.getDate() + 30);

      getCalendarEventsBetween(formatDate(today), formatDate(next30))
        .then((events) => {
          events.sort((a: any, b: any) => compareDateLike(a.date, b.date));
          setRecentEvents(events.slice(0, 5));
        })
        .catch(console.error);
    }
  }, [active, isSealUser, session.role]);

  const handleNavigate = (key: NavKey, _id?: string) => {
    if (session.role === "legacyDataEntry") return;
    setActive(key);
  };

  const handleNotifClick = (n: NotificationDoc) => {
    const mapping: Record<string, NavKey> = {
      contract: "contracts",
      case: "legalMatters",
      dispute: "legalMatters",
      brandAsset: "brandAssets",
      task: "tasksAndLogs",
      govCorrespondence: "govCorrespondence",
      seal: "sealApplication",
      governance: "governanceCoreDocs",
    };
    if (n.sourceType && mapping[n.sourceType]) {
      setActive(mapping[n.sourceType]);
    }
    setShowNotifPanel(false);
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
      setNotifs([]);
      showToast("已全部標示為已讀");
    } catch (e) {
      console.error(e);
      showToast("操作失敗", "error");
    }
  };

  const renderContent = () => {
    const email = (user?.email || "").toLowerCase();
    const isSpecialAdmin = ["kc@lg.app", "ad@lg.app"].includes(email);

    if (active === "legacyContracts") {
      if (session.role === "legacyDataEntry" || isSpecialAdmin) return <LegacyContractsPage />;
      return (
        <div className="p" style={{ padding: 40, textAlign: "center", color: "var(--danger)" }}>
          權限不足 (403)
        </div>
      );
    }

    if (session.role === "legacyDataEntry") return <LegacyContractsPage />;

    if (session.role === "dept-minimal") {
      const allowed: NavKey[] = [
        "governanceCoreDocs",
        "sealApplication",
        "contractReviews",
        "complianceControlCenter",
      ];
      if (!allowed.includes(active)) {
        return (
          <div className="p" style={{ padding: 40, textAlign: "center", color: "var(--danger)" }}>
            權限不足 (403): 此帳號僅限存取特定功能
          </div>
        );
      }
    }

    if (active === "tasksAndLogs" && !canViewWorklog(email)) {
      return (
        <div className="p" style={{ padding: 40, textAlign: "center", color: "var(--danger)" }}>
          權限不足 (403): 僅限法務/管理員存取
        </div>
      );
    }

    if (active === "audit" && !canViewAuditLog(email)) {
      return (
        <div className="p" style={{ padding: 40, textAlign: "center", color: "var(--danger)" }}>
          權限不足 (403): 僅限法務/管理員存取
        </div>
      );
    }

    if (active === "storageDebug" && !isSpecialAdmin) {
      return (
        <div className="p" style={{ padding: 40, textAlign: "center", color: "var(--danger)" }}>
          權限不足
        </div>
      );
    }

    switch (active) {
      case "dashboard":
        return !dashStats ? (
          <div className="p" style={{ padding: 20, textAlign: "center" }}>
            載入儀錶板數據...
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <LegalDashboardDailySummaryCard onNavigate={handleNavigate} />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                gap: 20,
              }}
            >
              <div className="tile">
                <div
                  style={{
                    borderBottom: "1px solid #333",
                    paddingBottom: 8,
                    marginBottom: 10,
                    fontWeight: "bold",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>合約總覽</span>
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>
                    Active: {dashStats.contracts.active}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-around", textAlign: "center" }}>
                  <div>
                    <div className="h1" style={{ color: "var(--ok)" }}>
                      {dashStats.contracts.active}
                    </div>
                    <div className="p">有效合約</div>
                  </div>
                  <div>
                    <div className="h1" style={{ color: "#fbbf24" }}>
                      {dashStats.contracts.expiringIn90Days}
                    </div>
                    <div className="p">90天內到期</div>
                  </div>
                  <div>
                    <div className="h1" style={{ color: "var(--danger)" }}>
                      {dashStats.contracts.highRiskAmount}
                    </div>
                    <div className="p">高額合約</div>
                  </div>
                </div>
              </div>

              <div className="tile">
                <div
                  style={{
                    borderBottom: "1px solid #333",
                    paddingBottom: 8,
                    marginBottom: 10,
                    fontWeight: "bold",
                  }}
                >
                  案件與法遵
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div style={{ textAlign: "center", background: "#222", padding: 10, borderRadius: 8 }}>
                    <div className="h1">{dashStats.cases.ongoing}</div>
                    <div className="p">進行中案件</div>
                  </div>
                  <div style={{ textAlign: "center", background: "#222", padding: 10, borderRadius: 8 }}>
                    <div className="h1" style={{ color: "#fbbf24" }}>
                      {dashStats.compliance.thisMonthCount}
                    </div>
                    <div className="p">本月應行專案</div>
                  </div>
                </div>
              </div>

              <div className="tile">
                <div
                  style={{
                    borderBottom: "1px solid #333",
                    paddingBottom: 8,
                    marginBottom: 10,
                    fontWeight: "bold",
                  }}
                >
                  待辦與簽核
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>待審合約草案</span>
                    <b style={{ color: "#8b5cf6" }}>{dashStats.reviews.pendingContracts}</b>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>待核用印</span>
                    <b style={{ color: "#8b5cf6" }}>{dashStats.reviews.pendingSealRequests}</b>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>待辦公文</span>
                    <b style={{ color: "#8b5cf6" }}>{dashStats.reviews.pendingGovLetters}</b>
                  </div>
                  <div
                    style={{
                      borderTop: "1px dashed #444",
                      marginTop: 4,
                      paddingTop: 4,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>未讀通知</span>
                    <b style={{ color: "var(--danger)" }}>{dashStats.notifications.unreadCount}</b>
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
                gap: 20,
              }}
            >
              <div className="section" style={{ minHeight: 250 }}>
                <div
                  style={{
                    borderBottom: "1px solid #333",
                    paddingBottom: 10,
                    marginBottom: 12,
                    fontWeight: "bold",
                    fontSize: 16,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>近期重要提醒 (30天內)</span>
                  <button className="smallbtn" style={{ fontSize: 11 }} onClick={() => setActive("calendar")}>
                    前往行事曆
                  </button>
                </div>
                {recentEvents.length === 0 ? (
                  <div className="p" style={{ textAlign: "center", marginTop: 20 }}>
                    近期無重要事項
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {recentEvents.map((e, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px 0",
                          borderBottom: "1px solid #333",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ textAlign: "center", minWidth: 40 }}>
                            <div style={{ fontSize: 11, color: "var(--muted)" }}>{formatDateMD(e.date)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 500 }}>{e.title}</div>
                            <div style={{ fontSize: 12, color: "var(--muted)" }}>
                              {e.note || e.eventType || "一般事件"}
                            </div>
                          </div>
                        </div>
                        {e.severity === "upcoming" && <span className="badge" style={{ color: "#fbbf24" }}>即將到期</span>}
                        {e.severity === "overdue" && <span className="badge" style={{ color: "var(--danger)" }}>已過期</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="section" style={{ minHeight: 250 }}>
                <div
                  style={{
                    borderBottom: "1px solid #333",
                    paddingBottom: 10,
                    marginBottom: 12,
                    fontWeight: "bold",
                    fontSize: 16,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>最新通知 (未讀)</span>
                  <button
                    className="smallbtn"
                    style={{ fontSize: 11 }}
                    onClick={() => setActive("notificationHistory")}
                  >
                    查看全部紀錄
                  </button>
                </div>
                {notifs.length === 0 ? (
                  <div className="p" style={{ textAlign: "center", marginTop: 20 }}>
                    目前沒有新通知
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {notifs.slice(0, 5).map((n) => (
                      <div
                        key={n.id}
                        onClick={() => handleNotifClick(n)}
                        style={{
                          cursor: "pointer",
                          padding: "8px",
                          borderRadius: 6,
                          background: "rgba(255,255,255,0.03)",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                          <span style={{ fontSize: 13, fontWeight: "bold", color: "#e5e7eb" }}>
                            {n.title}
                          </span>
                          <span style={{ fontSize: 11, color: "var(--muted)" }}>{n.date || n.eventDate}</span>
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--muted)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {n.detail}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case "legalMatters":
        return <LegalMattersPage />;
      case "ipMatters":
        return <IpMattersPage />;
      case "brandAssets":
        return <BrandAssetsPage />;
      case "lawyerCoop":
        return <LawyerCoopPage role={session.role} />;
      case "sealApplication":
        return <SealApplicationPage />;
      case "govCorrespondence":
        return <GovCorrespondencePage />;
      case "contracts":
        return <ContractsPage />;
      case "contractReviews":
        return <ContractReviewsPage />;
      case "calendar":
        return <CalendarPage onNavigate={handleNavigate} />;
      case "templates":
        return <TemplatesPage />;
      case "governanceCoreDocs":
        return <GovernanceCoreDocsPage />;
      case "tasksAndLogs":
        return <TasksAndLogsPage />;
      case "audit":
        return <AuditLogPage />;
      case "notificationHistory":
        return <NotificationHistoryPage />;
      case "storageDebug":
        return <SystemDebugPage onNavigate={handleNavigate} />;
      case "complianceControlCenter":
        return <ComplianceControlCenterPage />;
      default:
        return <div className="p">開發中</div>;
    }
  };

  return (
    <div className="shell">
      <Sidebar
        active={active}
        onSelect={(k) => {
          setActive(k);
          setActiveItem(null);
        }}
        role={session.role}
        username={session.displayName}
        onLogout={onLogout}
        onOpenSettings={() => {}}
        onOpenShortcuts={() => {}}
      />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <header
          style={{
            height: 64,
            borderBottom: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            background: "var(--bg)",
            gap: 20,
          }}
        >
          <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <GlobalSearchBar onNavigate={handleNavigate} />
          </div>

          <div style={{ position: "relative", flexShrink: 0 }}>
            <button
              onClick={() => setShowNotifPanel(!showNotifPanel)}
              style={{
                background: "transparent",
                border: "1px solid var(--border)",
                borderRadius: "50%",
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "var(--text)",
                fontSize: 18,
              }}
            >
              🔔
              {notifs.length > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -2,
                    right: -2,
                    background: "var(--danger)",
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: "bold",
                    borderRadius: "50%",
                    width: 18,
                    height: 18,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px solid var(--bg)",
                  }}
                >
                  {notifs.length > 9 ? "9+" : notifs.length}
                </span>
              )}
            </button>

            {showNotifPanel && (
              <div
                style={{
                  position: "absolute",
                  top: "120%",
                  right: 0,
                  width: 320,
                  maxHeight: 450,
                  overflowY: "auto",
                  background: "#1e1e1e",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  boxShadow: "0 10px 40px rgba(0,0,0,0.6)",
                  zIndex: 3000,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #333",
                    fontWeight: "bold",
                    fontSize: 14,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>通知中心</span>
                  {notifs.length > 0 && (
                    <button
                      className="smallbtn"
                      style={{ fontSize: 10, padding: "2px 6px", height: "auto" }}
                      onClick={handleMarkAllRead}
                    >
                      全部已讀
                    </button>
                  )}
                </div>

                <div style={{ flex: 1, overflowY: "auto" }}>
                  {notifs.length === 0 ? (
                    <div style={{ padding: 30, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
                      目前沒有未讀通知
                    </div>
                  ) : (
                    notifs.map((n) => (
                      <div
                        key={n.id}
                        onClick={() => handleNotifClick(n)}
                        style={{
                          padding: "12px 16px",
                          borderBottom: "1px solid #333",
                          cursor: "pointer",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#2a2a2a")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span className="badge" style={{ fontSize: 10, padding: "2px 6px" }}>
                            {n.category}
                          </span>
                          <span style={{ fontSize: 11, color: "var(--muted)" }}>{n.eventDate}</span>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: "#e5e7eb", marginBottom: 2 }}>
                          {n.title}
                        </div>
                        <div style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.4 }}>{n.detail}</div>
                      </div>
                    ))
                  )}
                </div>

                <div style={{ borderTop: "1px solid #333", padding: 8, textAlign: "center" }}>
                  <button
                    className="smallbtn"
                    style={{ width: "100%", background: "transparent", border: "none", color: "var(--muted)" }}
                    onClick={() => {
                      setActive("notificationHistory");
                      setShowNotifPanel(false);
                    }}
                  >
                    查看歷史通知紀錄
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        <div className="main" style={{ flex: 1, overflowY: "auto", padding: "18px" }}>
          <div className="lop-page-header" style={{ marginBottom: 20 }}>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: "bold", color: "#fff" }}>
              {TITLES[active]?.title}
            </h2>
            <div style={{ fontSize: 13, color: "var(--muted)" }}>{TITLES[active]?.desc}</div>
          </div>
          {renderContent()}
        </div>
      </div>

      <AIAssistantDrawer isOpen={isAiOpen} onClose={() => setAiOpen(false)} context={active} />

      {!isSealUser && session.role !== "dept-minimal" && <AiFloatingButton />}

      {deleteDialogState && (
        <ConfirmDeleteModal
          isOpen={!!deleteDialogState}
          title="確認刪除"
          message={`您確定要刪除「${deleteDialogState.summary}」嗎？`}
          onConfirm={() => {
            deleteDialogState.onConfirm();
            closeDeleteDialog();
          }}
          onCancel={closeDeleteDialog}
        />
      )}
    </div>
  );
}

export function Shell(props: { session: Session; onLogout: () => void; bootErr?: any }) {
  return (
    <ToastProvider>
      <GlobalProvider>
        <ShellInner {...props} />
      </GlobalProvider>
    </ToastProvider>
  );
}
